import React, { useState } from 'react'

/**
 * CardFrame — the unifying primitive of every prep, in-session, and overview card.
 *
 * Composition:
 *  - 3px left accent rule (moss for prep, clay for session, none for overview)
 *  - Icon + title header, optional `headerRight` ribbon (e.g. "core")
 *  - Italic-serif prompt with dashed-rule separator
 *  - Flexible body (children)
 *  - Optional back face (history): set `back` prop with the back-face node.
 *    A flip toggle button appears top-right when `back` is provided.
 *  - `error` state: rust ring/shadow (e.g. missing required field)
 *  - `locked` state: dimmed paper-deep background + LOCKED mono chip top-right
 *
 * Spec: claude_design/design-system/primitives.html §8.1
 * Phase E adds: back slot, error state, locked state, flip toggle.
 */
export function CardFrame({
  kind = 'prep',
  icon,
  iconClass = 'moss',
  title,
  titleAriaLabel,
  onTitleClick,
  prompt,
  headerRight,
  state = 'empty',
  flippable = false,
  back,
  as: Tag = 'section',
  className = '',
  children,
  ...rest
}) {
  const [showBack, setShowBack] = useState(false)

  // When back is provided, drive the state from the flip toggle
  const effectiveState = back && showBack ? 'back' : state

  const classes = [
    'cardframe',
    `cardframe--${kind}`,
    `cardframe--${effectiveState}`,
    flippable && 'cardframe--flippable',
    className,
  ].filter(Boolean).join(' ')

  // Locked state shows a "LOCKED" mono chip in place of the flip button
  const lockedChip = effectiveState === 'locked' && (
    <span className="cardframe__locked-chip" aria-label="Card locked">LOCKED</span>
  )

  // Flip toggle — only shown when back face is provided and card is not locked
  const flipToggle = back && effectiveState !== 'locked' && (
    <button
      type="button"
      className="cardframe__flip-toggle"
      onClick={() => setShowBack((v) => !v)}
      aria-label={showBack ? `Back to ${title}` : `View ${title} history`}
      aria-pressed={showBack}
    >
      {showBack ? '↩' : '⟳'}
    </button>
  )

  // When `onTitleClick` is set, render icon+title as a button so the card's
  // history flip (or any title-affordance) is keyboard-accessible. Otherwise
  // they remain plain spans.
  const iconNode = icon && (
    <span className={`cardframe__icon cardframe__icon--${iconClass}`} aria-hidden="true">
      {icon}
    </span>
  )
  const titleNode = title && <span className="cardframe__title">{title}</span>

  // Right-side header slot: locked chip OR flip toggle OR caller-supplied headerRight
  const resolvedHeaderRight = lockedChip || flipToggle
    || (headerRight != null && <span className="cardframe__header-right">{headerRight}</span>)
    || null

  return (
    <Tag className={classes} data-kind={kind} data-state={effectiveState} {...rest}>
      {(icon || title || resolvedHeaderRight) && (
        <header className="cardframe__head">
          {onTitleClick && !showBack ? (
            <button
              type="button"
              className="cardframe__title-button"
              onClick={onTitleClick}
              aria-label={titleAriaLabel}
            >
              {iconNode}
              {titleNode}
            </button>
          ) : (
            <>
              {iconNode}
              {titleNode}
            </>
          )}
          {resolvedHeaderRight}
        </header>
      )}
      {!showBack && prompt && <p className="cardframe__prompt">{prompt}</p>}
      {showBack && back
        ? <div className="cardframe__body cardframe__body--back">{back}</div>
        : children != null && <div className="cardframe__body">{children}</div>
      }
    </Tag>
  )
}
